/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { createRef, ComponentType } from 'preact';
import { useEffect, forwardRef, Ref } from 'preact/compat';
import { EnvironmentProvider } from '#UNSAFE_Environment';
import {
  ThemeModifiers as Props,
  DARK_CLASS,
  INVERT_CLASS,
  LIGHT_CLASS,
  COLORSCHEME_DEPENDENT_CLASS,
  SCALE_SM_CLASS,
  SCALE_MD_CLASS,
  SCALE_LG_CLASS,
  SCALE_DEPENDENT_CLASS,
  DENSITY_COMPACT_CLASS,
  DENSITY_STANDARD_CLASS,
  DENSITY_DEPENDENT_CLASS
} from '#UNSAFE_Theme';

/**
 * Higher Order Component that allows you to wrap an existing component with an EnvironmentProvider
 * containing the desired colorScheme, scale, and density context. The returned component contains all the props
 * of the wrapped component with the addition of the `colorScheme`, `scale`, and `density` props.
 *
 * This should be used when nesting an alternate colorScheme (ie dark palette sub-form within a global light
 * palette). You can use this HOC instead of manually applying colorScheme classnames to DOM elements. This also
 * eliminates the need to add additional DOM wrapper elements.
 */
const withThemeProps = <T extends object>(WrappedComponent: ComponentType<T>) => {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'ComponentWithProvider';

  const ComponentWithProvider = forwardRef(
    ({ colorScheme, scale, density, ...props }: T & Partial<Props>, ref) => {
      // if no ref is supplied, create one
      const wrapRef = (ref || createRef()) as Ref<unknown>;

      useEffect(() => {
        if (wrapRef.current) {
          const { classList } = wrapRef.current as HTMLElement;
          if (!classList) return;

          // dynamically apply the appropriate colorscheme classname to ref element
          if (colorScheme) {
            const schemes = [
              LIGHT_CLASS,
              DARK_CLASS,
              INVERT_CLASS,
              COLORSCHEME_DEPENDENT_CLASS
            ] as const;
            schemes.map((current) => classList.remove(current));

            if (colorScheme === 'dark') {
              classList.add(DARK_CLASS);
              classList.add(INVERT_CLASS);
            } else {
              classList.add(LIGHT_CLASS);
            }
            classList.add(COLORSCHEME_DEPENDENT_CLASS);
          }

          // dynamically apply the appropriate scale classname to ref element
          if (scale) {
            const scales = [SCALE_SM_CLASS, SCALE_MD_CLASS, SCALE_LG_CLASS, SCALE_DEPENDENT_CLASS];
            scales.map((current) => classList.remove(current));
            classList.add(
              `${
                scale === 'sm' ? SCALE_SM_CLASS : scale === 'md' ? SCALE_MD_CLASS : SCALE_LG_CLASS
              }`
            );
            classList.add(SCALE_DEPENDENT_CLASS);
          }

          // dynamically apply the appropriate density classname to ref element
          if (density) {
            const densities = [
              DENSITY_COMPACT_CLASS,
              DENSITY_STANDARD_CLASS,
              DENSITY_DEPENDENT_CLASS
            ];
            densities.map((current) => classList.remove(current));
            classList.add(
              `${density === 'compact' ? DENSITY_COMPACT_CLASS : DENSITY_STANDARD_CLASS}`
            );
            classList.add(DENSITY_DEPENDENT_CLASS);
          }
        }
      }, [wrapRef, colorScheme, scale, density]);

      // wrap component with new colorscheme context
      return (
        <EnvironmentProvider environment={{ colorScheme, scale, density }}>
          <WrappedComponent {...(props as T)} ref={wrapRef} />
        </EnvironmentProvider>
      );
    }
  );

  ComponentWithProvider.displayName = `withThemeProps(${displayName})`;

  return ComponentWithProvider;
};

export default withThemeProps;
